import { type CSSProperties, useState } from 'react';
import { getAssetUrl } from '@/shared/utils';

/**
 * User type (minimal interface for avatar)
 */
export interface AvatarUser {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  avatarUrl?: string | null;
}

/**
 * Avatar size variants
 */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Size configuration mapping
 */
const sizeConfig: Record<AvatarSize, { container: string; text: string }> = {
  xs: { container: 'h-6 w-6', text: 'text-xs' },
  sm: { container: 'h-8 w-8', text: 'text-sm' },
  md: { container: 'h-10 w-10', text: 'text-base' },
  lg: { container: 'h-12 w-12', text: 'text-lg' },
  xl: { container: 'h-16 w-16', text: 'text-xl' },
};

export interface UserAvatarProps {
  /** User object with name and avatar */
  user: AvatarUser;

  /** Size variant */
  size?: AvatarSize;

  /** Show online status indicator */
  showStatus?: boolean;

  /** Custom className */
  className?: string;
}

/**
 * Get user initials from first and last name
 */
const getUserInitials = (user: AvatarUser): string => {
  const first = user.firstName?.charAt(0)?.toUpperCase() || '';
  const last = user.lastName?.charAt(0)?.toUpperCase() || '';

  if (first && last) return `${first}${last}`;
  if (first) return first;
  if (last) return last;

  // Fallback to first letter of email
  return user.email?.charAt(0).toUpperCase() || '?';
};

/**
 * Generate consistent color from string (for initials background)
 */
const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  return `hsl(${hue}, 70%, 60%)`;
};

/**
 * UserAvatar Component
 * Displays user avatar image or initials fallback
 * Theme-aware styling with consistent colors
 */
export const UserAvatar = ({
  user,
  size = 'md',
  showStatus = false,
  className = '',
}: UserAvatarProps) => {
  // Track if image failed to load (for fallback to initials)
  // Must be before any early returns to follow React hooks rules
  const [imageError, setImageError] = useState(false);

  // Guard against undefined user
  if (!user) {
    return (
      <div className={`flex items-center justify-center bg-gray-400 text-white font-bold ${className}`}>
        ?
      </div>
    );
  }

  const initials = getUserInitials(user);
  const backgroundColor = stringToColor(user.email || 'default');
  const { container, text } = sizeConfig[size];

  // Status indicator size based on avatar size
  const statusSize = size === 'xs' ? 'h-1.5 w-1.5' : size === 'sm' ? 'h-2 w-2' : 'h-3 w-3';

  const containerStyle: CSSProperties = {
    border: `var(--theme-border-width) solid var(--theme-border)`,
    borderRadius: 'var(--theme-border-radius)',
    boxShadow: 'var(--theme-shadow)',
  };

  // Show initials if no avatar URL or if image failed to load
  const showInitials = !user.avatarUrl || imageError;

  return (
    <div className={`relative inline-flex ${className}`}>
      {/* Avatar container */}
      {!showInitials ? (
        // Avatar image
        <img
          src={getAssetUrl(user.avatarUrl) || ''}
          alt={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
          className={`${container} rounded-full object-cover transition-all duration-300`}
          style={containerStyle}
          onError={() => setImageError(true)}
        />
      ) : (
        // Initials fallback
        <div
          className={`${container} rounded-full flex items-center justify-center font-semibold text-white transition-all duration-300`}
          style={{
            ...containerStyle,
            backgroundColor,
          }}
        >
          <span className={text}>{initials}</span>
        </div>
      )}

      {/* Online status indicator */}
      {showStatus && (
        <span
          className={`absolute bottom-0 right-0 ${statusSize} bg-green-400 border-2 border-white rounded-full`}
          style={{
            borderColor: 'var(--theme-surface)',
          }}
        />
      )}
    </div>
  );
};
